/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {
  MenuItem,
  Paper,
  Popper,
  PopperPlacementType
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import {useSignals} from '@preact/signals-react/runtime';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import Render from '@ion/dls/src/render/Render';
import {DLSTheme} from '@ion/dls/src/types/types';
import changePage, {getRoute, Route} from '@/routes/RouteDefinitions';
import {
  isEnabled as isPegWizardEnabled,
  setShowPegWizard,
  setWizardReturnRoute
} from '@/routes/toolbox/pegwizard/store';
import {setPage} from '@/store/actions/nav';
import ClickAwayListener from '../../components/listeners/ClickAwayListener';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {DropdownArrowIcon} from '../../icons/DropdownArrowIcon';
import {dlsColor, dlsZIndex} from '../../theme/theme';
import Tooltip from '../tooltip/Tooltip';
import {PrimaryButton} from './PrimaryButton';

interface PopupPrimaryButtonProps {
  onAddPrimary?: MouseEventHandler;
  onAddSecondary?: MouseEventHandler;
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  entity?: string;
  addEnabled?: boolean;
  primaryActionAllowed?: boolean;
  secondaryActionAllowed?: boolean;
  secondaryAddAllowed?: boolean;
  hideOption?: string;
  tooltipTitle?: string;
};

const useStyles = (theme: DLSTheme) => ({
  paperSection : {
    width   : '133px',
    height  : '75px',
    display : 'grid'
  },
  dropDownItem : {
    marginLeft    : 5,
    textTransform : 'capitalize',
    paddingRight  : '5px'
  },
  newBadge : {
    background    : '#c218f0',
    color         : '#fff',
    fontSize      : '8px',
    fontWeight    : '700',
    padding       : '2px 6px',
    borderRadius  : '3px',
    letterSpacing : '0.4px',
    lineHeight    : '1.2',
    textTransform : 'uppercase'
  },
  pegWizardMenuItem : {
    borderTop : `1px solid ${dlsColor('grey4')}`
  }
});

/**
 * The PopupPrimaryButton.
 *
 * ```typescript
 *
 * import {PopupPrimaryButton} from '@ion/dls/src/components/buttons';
 *
 * <PopupPrimaryButton deviceFile{deviceFile} />
 *
 * ```
 *
 */
export const PopupPrimaryButton = ({
  onAddPrimary,
  onAddSecondary,
  styleOverrides,
  entity = '',
  hideOption = '',
  tooltipTitle = '',
  addEnabled = false,
  secondaryAddAllowed = false,
  primaryActionAllowed = false,
  secondaryActionAllowed = false
}: PopupPrimaryButtonProps) => {
  const dispatch                  = useDispatch();
  const popupStyles               = useExtendStyles(useStyles, styleOverrides);
  const classes                   = popupStyles();
  const buttonRef                 = useRef(null);
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [anchorEl, setAnchorEl]   = useState<HTMLButtonElement | null>(null);
  const [open, setOpen]           = useState(false);
  const options                   = translate('devicefiles:editors.fileActions', {returnObjects: true});
  useSignals();

  const handleClick =
    (newPlacement: PopperPlacementType) =>
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setAnchorEl(buttonRef?.current);
        setOpen((prev) => placement !== newPlacement || !prev);
        setPlacement(newPlacement);
      };

  const handleClose = useCallback(function() {
    setOpen(false);
  }, []);
  
  const navigateToPegWizard = useCallback(function() {
    setWizardReturnRoute?.(Route.PROVISIONING_DEVICE_FILES);
    setShowPegWizard?.(true);
    dispatch(setPage(getRoute(Route.TOOLBOX_PEG_WIZARD)));
    changePage(Route.TOOLBOX_PEG_WIZARD);
  }, [dispatch]);
  
  return (
    <>
      <Popper transition sx={dlsZIndex('floatingActionButtonDisplay')}
        open={open}
        anchorEl={anchorEl}
        placement={placement}>
        <ClickAwayListener mountListener onClickAway={handleClose}>
          <Paper className={classes.paperSection}>
            <Render.If condition={secondaryAddAllowed}>
              <MenuItem onClick={onAddSecondary}>
                <span className={classes.dropDownItem}>{options?.config}</span>
              </MenuItem>
            </Render.If>
            <Render.If condition={isPegWizardEnabled}>
              <MenuItem className={classnames({[classes.pegWizardMenuItem]: secondaryAddAllowed})}
                onClick={navigateToPegWizard}>
                <span className={classes.dropDownItem}>{options?.pegWizard}</span>
                <span className={classes.newBadge}>{translate('devicefiles:new')}</span>
              </MenuItem>
            </Render.If>
          </Paper>
        </ClickAwayListener>
      </Popper>
      <Tooltip arrow title={primaryActionAllowed && translate(`${tooltipTitle}`, {entity: entity})}
        disableHoverListener={!primaryActionAllowed}>
        <div ref={buttonRef}>
          <PrimaryButton startIcon={<Add />}
            endIcon={<Render.If condition={secondaryActionAllowed && (secondaryAddAllowed || isPegWizardEnabled())}>
              <div onClick={handleClick('bottom-start')} style={{cursor: 'pointer'}}>
                <DropdownArrowIcon color={dlsColor('white')} />
              </div>
            </Render.If>}
            className={
              classnames({
                [hideOption] : !secondaryActionAllowed && !addEnabled
              })
            } disabled={primaryActionAllowed} onClick={onAddPrimary}>
            {translate('common:addNew')}
          </PrimaryButton>
        </div>
      </Tooltip>
    </>
  );
};

export default React.memo(PopupPrimaryButton);
